import { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  Column,
  DatePicker,
  InputCheckbox,
  InputText,
  ModalForm,
  Row,
  Select,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { formatHours, parseHours } from "@components/ow";
import { isoToDate, localIsoDate } from "@helpers/localDate";
import { focusFirstError } from "../../Common/FormPage";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || "20px"};
`;

// $centralize centraliza o calendário no ancestral posicionado, e o Column da lib é
// relative: sem position, ele centraliza pela linha toda (a largura do modal).
const StaticColumn = styled(Column)`
  position: static;
`;

const DESCRIPTION_MAX = 120;
const FIELDS = ["day-off-date", "day-off-description", "day-off-hours"];
const PERIOD_OPTIONS = [
  { label: "Dia inteiro", value: "FULL" },
  { label: "Algumas horas", value: "PARTIAL" },
];

function initialValues(dayOff) {
  if (!dayOff) return { date: "", description: "", period: "FULL", hours: "", repeatsYearly: false };
  return {
    date: dayOff.date,
    description: dayOff.description,
    period: dayOff.hours_off ? "PARTIAL" : "FULL",
    hours: dayOff.hours_off ? formatHours(dayOff.hours_off * 60) : "",
    repeatsYearly: Boolean(dayOff.repeats_yearly),
  };
}

function validate(values) {
  const errors = {};
  if (!values.date) errors["day-off-date"] = "Informe a data";
  if (!values.description.trim()) errors["day-off-description"] = "Informe a descrição";
  if (values.period === "PARTIAL") {
    const minutes = parseHours(values.hours);
    if (minutes === null || minutes <= 0 || minutes > 24 * 60) errors["day-off-hours"] = "Informe as horas, até 24h";
  }
  return errors;
}

/**
 * Cadastro e edição de uma folga da empresa (só ADM). Só de hoje em diante: as
 * competências que já passaram não são recalculadas.
 *
 * Props: dayOff (null para cadastrar), onClose, onSaved.
 */
export default function DayOffModal({ dayOff, onClose, onSaved }) {
  const dayOffService = useService("companyDayOffs");
  const [values, setValues] = useState(() => initialValues(dayOff));
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const today = isoToDate(localIsoDate());

  const errors = submitted ? validate(values) : {};
  const set = (field, value) => setValues((prev) => ({ ...prev, [field]: value }));

  function close() {
    if (!saving) onClose();
  }

  async function save() {
    setSubmitted(true);
    const found = validate(values);
    if (Object.keys(found).length) {
      focusFirstError(FIELDS.filter((id) => found[id]));
      return;
    }

    // A tabela guarda as horas com duas casas
    const hoursOff = values.period === "PARTIAL" ? Math.round((parseHours(values.hours) / 60) * 100) / 100 : null;
    const payload = {
      date: values.date,
      description: values.description.trim(),
      hours_off: hoursOff,
      repeats_yearly: values.repeatsYearly,
    };

    setSaving(true);
    try {
      if (dayOff) {
        await dayOffService.updateDayOff(dayOff.id, payload);
        toast.success("Folga atualizada");
      } else {
        await dayOffService.createDayOff(payload);
        toast.success("Folga cadastrada");
      }
      setSaving(false);
      onSaved();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.msg || "Erro ao salvar a folga");
      setSaving(false);
    }
  }

  return (
    <ModalForm
      $showModal
      $setShowModal={close}
      $noClose={saving}
      $isLoading={saving}
      $size="medium"
      $title={dayOff ? "Editar folga" : "Nova folga da empresa"}
      $contentBackground="N0"
      $actions={[
        { text: "Cancelar", action: close, variant: "outline", isDisabled: saving },
        { text: dayOff ? "Salvar folga" : "Cadastrar folga", action: save, variant: "primary", isDisabled: saving },
      ]}>
      <Stack>
        <Row $gap={20}>
          <StaticColumn $columns={5}>
            <DatePicker
              $idForm="day-off-date"
              $centralize
              $label="Data"
              $variant="default"
              $isRequired
              $hasTooltip="Só de hoje em diante: as competências que já passaram não são recalculadas."
              $errors={errors["day-off-date"]}
              $minDate={today}
              $date={isoToDate(values.date)}
              $setDate={(date) => set("date", localIsoDate(date) || "")}
            />
          </StaticColumn>
          <Column $columns={7}>
            <InputText
              $idForm="day-off-description"
              $label="Descrição"
              $variant="default"
              $isRequired
              $hasTooltip="É o texto que aparece no espelho de ponto do colaborador."
              $errors={errors["day-off-description"]}
              maxLength={DESCRIPTION_MAX}
              placeholder="Ex.: Revolução Constitucionalista"
              value={values.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </Column>
        </Row>

        <Row $gap={20}>
          <Column $columns={5}>
            <Select
              $idForm="day-off-period"
              $label="Folga"
              $isRequired
              $options={PERIOD_OPTIONS}
              value={PERIOD_OPTIONS.find((option) => option.value === values.period)}
              onChange={(option) => option && set("period", option.value)}
            />
          </Column>
          {values.period === "PARTIAL" ? (
            <Column $columns={7}>
              <InputText
                $idForm="day-off-hours"
                $label="Horas de folga"
                $variant="default"
                $isRequired
                $hasTooltip="Descontadas da jornada de cada contrato: com jornada de 8h e 2h de folga, o dia espera 6h."
                $errors={errors["day-off-hours"]}
                placeholder="Ex.: 2h ou 1h30"
                value={values.hours}
                onChange={(e) => set("hours", e.target.value)}
              />
            </Column>
          ) : null}
        </Row>

        <Stack $gap="4px">
          <InputCheckbox
            key={`day-off-repeats-${values.repeatsYearly}`}
            $options={[{ name: "day-off-repeats", label: "Repete todo ano", value: values.repeatsYearly }]}
            onChange={(e) => e.target.name === "day-off-repeats" && set("repeatsYearly", e.target.checked)}
          />
          <Text
            $text="Se marcado, a folga vale todo ano no mesmo dia, como o aniversário da empresa."
            $variant="caption_medium"
            $color="N400"
          />
        </Stack>
      </Stack>
    </ModalForm>
  );
}
